import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, BellRing, CheckCircle2, Plus, QrCode, Wallet } from 'lucide-react';
import { ApiError, errorMessage, post } from '../../lib/api';
import { formatBRL, formatDateTime } from '../../lib/format';
import { Alert, Button, Card, Spinner } from '../../ui/primitives';
import { useToast } from '../../ui/feedback';
import { OrderProgress, OrderStatusBadge } from '../../menu/OrderStatus';
import type { TenantRestaurant } from '../TenantApp';
import { useGuestSession } from './guest';
import { useGuestRealtime } from '../../lib/realtime';
import { publicPath } from '../../lib/paths';

/** "Minha mesa": pedidos da comanda (de todos na mesa), andamento e total consumido. Atualiza sozinho. */
export function MyTable({ restaurant }: { restaurant: TenantRestaurant }) {
  const q = useGuestSession(true);
  useGuestRealtime(q.isSuccess);
  const qc = useQueryClient();
  const toast = useToast();
  const sent = (useLocation().state as { sent?: number } | null)?.sent;
  const [busy, setBusy] = useState(false);

  if (q.isPending) return <Spinner />;
  if (q.isError) {
    const closed = q.error instanceof ApiError && q.error.status === 410;
    return (
      <main className="grid min-h-dvh place-items-center px-4 text-center">
        <div className="max-w-sm">
          <div className="mx-auto grid size-12 place-items-center rounded-2xl bg-paper-2 text-ink-3"><QrCode className="size-6" /></div>
          <h1 className="mt-3 text-lg font-semibold">{closed ? 'Esta comanda foi encerrada' : 'Nenhuma mesa identificada'}</h1>
          <p className="mt-1 text-sm text-ink-3">{closed ? 'Obrigado pela visita! Para um novo pedido, escaneie o QR Code da mesa.' : 'Escaneie o QR Code da sua mesa para acompanhar seus pedidos.'}</p>
          {closed && <Link to={publicPath('/saida')} className="mt-6 block"><Button variant="brand" className="w-full">Ver comprovante de saída</Button></Link>}
          <Link to={publicPath()} className="mt-6 inline-block text-sm font-medium text-[var(--brand)] underline">Voltar ao cardápio</Link>
        </div>
      </main>
    );
  }

  const { session, orders, totals } = q.data;
  const requestBill = async () => {
    setBusy(true);
    try {
      await post('/api/public/session/bill-request');
      await qc.invalidateQueries({ queryKey: ['guest-session'] });
      toast.success('A equipe foi avisada. Já já alguém vem até a mesa.');
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto min-h-dvh max-w-2xl px-4 pb-10 pt-4">
      <Link to={publicPath()} className="inline-flex items-center gap-1 text-sm text-ink-3 hover:text-ink"><ArrowLeft className="size-4" /> Cardápio</Link>
      <h1 className="mt-3 font-display text-2xl font-semibold">Mesa {session.tableLabel}</h1>
      <p className="text-sm text-ink-3">{restaurant.name} · comanda nº {session.number}</p>

      {sent && (
        <div className="mt-4">
          <Alert tone="success" title={`Pedido nº ${String(sent).padStart(4, '0')} enviado!`}>Acompanhe o preparo aqui. Esta página se atualiza sozinha.</Alert>
        </div>
      )}

      <Card className="mt-5 p-5">
        <div className="flex items-baseline justify-between">
          <span className="text-sm text-ink-3">Total consumido na mesa</span>
          <span className="font-display text-2xl font-semibold tabular-nums">{formatBRL(totals.consumedCents)}</span>
        </div>
        <p className="mt-1 text-xs text-ink-3">{totals.itemCount} item(ns) · inclui pedidos de todos na mesa</p>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <Link to={publicPath()} className="flex-1"><Button variant="secondary" className="w-full" icon={<Plus className="size-4" />}>Pedir mais</Button></Link>
          {session.billRequestedAt ? (
            <div className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-sprout-100 px-3 text-sm font-medium text-sprout-600">
              <CheckCircle2 className="size-4" /> Conta solicitada
            </div>
          ) : (
            <Button variant="brand" className="flex-1" loading={busy} disabled={totals.consumedCents === 0} onClick={requestBill} icon={<BellRing className="size-4" />}>
              Pedir a conta
            </Button>
          )}
        </div>
        {totals.consumedCents > 0 && (
          <Link to={publicPath('/conta')} className="mt-2 block">
            <Button variant="secondary" className="w-full" icon={<Wallet className="size-4" />}>Ver conta e pagar</Button>
          </Link>
        )}
        <p className="mt-3 text-xs text-ink-3">Pague tudo, só os seus itens ou um valor livre — pelo celular (quando disponível) ou com a equipe.</p>
      </Card>

      <h2 className="mt-8 font-semibold">Pedidos</h2>
      {orders.length === 0 ? (
        <p className="mt-2 text-sm text-ink-3">Nenhum pedido ainda.</p>
      ) : (
        <ul className="mt-3 space-y-3">
          {orders.map((o) => (
            <li key={o.id}>
              <Card className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold">Pedido nº {String(o.number).padStart(4, '0')}</p>
                    <p className="text-xs text-ink-3">
                      {formatDateTime(o.createdAt, restaurant.timezone)}
                      {o.mine ? ' · seu' : o.byStaff ? ' · lançado pela equipe' : o.customerName ? ` · ${o.customerName}` : ''}
                    </p>
                  </div>
                  <OrderStatusBadge status={o.status} />
                </div>
                <OrderProgress status={o.status} />
                <ul className="mt-3 space-y-1.5 border-t border-line pt-3 text-sm">
                  {o.items.map((i) => (
                    <li key={i.id} className={`flex justify-between gap-3 ${i.status === 'CANCELLED' ? 'text-ink-3 line-through' : ''}`}>
                      <span className="min-w-0">
                        {i.quantity}× {i.productName}
                        {i.options.length > 0 && <span className="block text-xs text-ink-3">{i.options.map((op) => op.optionName).join(' · ')}</span>}
                        {i.notes && <span className="block text-xs italic text-ink-3">“{i.notes}”</span>}
                      </span>
                      <span className="shrink-0 tabular-nums">{i.unitPriceCents != null && formatBRL(i.unitPriceCents * i.quantity)}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
